import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { AlertTriangle, MapPin, Anchor } from "lucide-react";
import { addNoaaLayer, setNoaaVisibility, firstSymbolLayerId } from "@/lib/noaaLayer";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (TOKEN) {
    mapboxgl.accessToken = TOKEN;
}

/**
 * FleetMap
 * -----------------------------------------------------------------------------
 * Dark-themed live map of every vessel that has numeric lat/lng.
 * • Pin shape: diamond, hollow-fill, 2px border in the vessel-state colour.
 * • Callsign floats below the pin on hover-ready label.
 * • Click a pin → onSelect(vessel).
 * • Auto-fits bounds when data changes.
 * • Vessels without coords surface in a badge (never silently dropped).
 */

const isNum = (n) => Number.isFinite(n);

export default function FleetMap({ vessels = [], stateMeta = {}, onSelect }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const roRef = useRef(null);
    const timersRef = useRef([]);
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;
    // Bumped after the map instance is created so the markers-sync effect
    // (which reads mapRef.current) will re-run once the map is live.
    const [mapReady, setMapReady] = useState(0);
    // NOAA nautical chart overlay — default ON for Fleet (maritime ops focus).
    const [noaaOn, setNoaaOn] = useState(true);

    // Initialise map once the container has actual dimensions.
    useEffect(() => {
        if (!TOKEN || !containerRef.current || mapRef.current) return;

        let cancelled = false;
        const init = () => {
            if (cancelled || mapRef.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            // If the container hasn't received a real height yet, defer one more frame.
            if (rect.height < 40) {
                requestAnimationFrame(init);
                return;
            }
            const map = new mapboxgl.Map({
                container: containerRef.current,
                style: "mapbox://styles/mapbox/dark-v11",
                center: [-70.75, 43.09],
                zoom: 4.5,
                attributionControl: false,
                projection: "mercator",
            });
            map.addControl(
                new mapboxgl.NavigationControl({ showCompass: false, visualizePitch: false }),
                "top-right"
            );
            map.addControl(
                new mapboxgl.ScaleControl({ maxWidth: 120, unit: "nautical" }),
                "bottom-right"
            );
            mapRef.current = map;

            const safeResize = () => {
                const m = mapRef.current;
                // eslint-disable-next-line no-underscore-dangle
                if (!m || m._removed) return;
                try {
                    m.resize();
                } catch (_) {}
            };
            map.on("load", safeResize);
            map.on("styledata", safeResize);
            map.once("idle", safeResize);
            // Add the NOAA nautical chart overlay once the base style has loaded.
            map.once("load", () => {
                addNoaaLayer(map, {
                    visible: true,
                    opacity: 0.85,
                    beforeLayerId: firstSymbolLayerId(map),
                });
            });
            // Belt-and-braces: mapbox occasionally locks in an intermediate
            // canvas height when React swaps view modes mid-layout. Force a
            // resize on the next few animation frames after construction.
            const t1 = setTimeout(safeResize, 50);
            const t2 = setTimeout(safeResize, 250);
            const t3 = setTimeout(safeResize, 800);
            timersRef.current = [t1, t2, t3];

            const ro = new ResizeObserver(safeResize);
            ro.observe(containerRef.current);
            roRef.current = ro;

            // Signal to the markers effect that the map is now live.
            setMapReady((n) => n + 1);
        };
        requestAnimationFrame(init);

        return () => {
            cancelled = true;
            timersRef.current.forEach(clearTimeout);
            timersRef.current = [];
            if (roRef.current) {
                roRef.current.disconnect();
                roRef.current = null;
            }
            markersRef.current.forEach((m) => m.remove());
            markersRef.current = [];
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    // Sync markers whenever the vessel list changes.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        // Wipe stale markers.
        markersRef.current.forEach((m) => m.remove());
        markersRef.current = [];

        const geo = vessels.filter((v) => isNum(v.lat) && isNum(v.lng));

        geo.forEach((v) => {
            const meta = stateMeta[v.state] || { bg: "#FF5500", label: v.state };

            // Wrapper (unrotated) — mapbox anchors this element; label stays legible.
            const wrap = document.createElement("div");
            wrap.style.position = "relative";
            wrap.style.width = "22px";
            wrap.style.height = "22px";
            wrap.style.cursor = "pointer";
            wrap.setAttribute("data-testid", `fleet-map-pin-${v.id}`);

            // Diamond pin (rotated 45deg).
            const pin = document.createElement("div");
            pin.style.width = "18px";
            pin.style.height = "18px";
            pin.style.position = "absolute";
            pin.style.top = "2px";
            pin.style.left = "2px";
            pin.style.border = `2px solid ${meta.bg}`;
            pin.style.background = "rgba(5,5,5,0.85)";
            pin.style.transform = "rotate(45deg)";
            pin.style.boxShadow = `0 0 12px ${meta.bg}80`;
            wrap.appendChild(pin);

            // Center orange dot (pulse for lost-comms).
            if (v.state === "lost-comms") {
                const dot = document.createElement("div");
                dot.style.width = "6px";
                dot.style.height = "6px";
                dot.style.position = "absolute";
                dot.style.top = "8px";
                dot.style.left = "8px";
                dot.style.background = meta.bg;
                dot.style.borderRadius = "50%";
                dot.style.animation = "pulse-dot 1.6s ease-in-out infinite";
                wrap.appendChild(dot);
            }

            // Floating callsign label — hidden by default, revealed on hover
            // (prevents label pile-up when vessels are geographically close).
            const label = document.createElement("div");
            label.textContent = `#${String(v.index || "").padStart(2, "0")} ${v.callsign || ""}`;
            label.style.position = "absolute";
            label.style.top = "26px";
            label.style.left = "50%";
            label.style.transform = "translateX(-50%)";
            label.style.fontFamily = "'JetBrains Mono', monospace";
            label.style.fontSize = "10px";
            label.style.letterSpacing = "0.18em";
            label.style.color = "#FFFFFF";
            label.style.textShadow = "0 0 6px #000, 0 0 4px #000";
            label.style.whiteSpace = "nowrap";
            label.style.pointerEvents = "none";
            label.style.opacity = "0";
            label.style.transition = "opacity 0.15s ease-out";
            label.style.background = "rgba(5,5,5,0.72)";
            label.style.padding = "1px 6px";
            label.style.border = `1px solid ${meta.bg}`;
            wrap.appendChild(label);
            wrap.addEventListener("mouseenter", () => (label.style.opacity = "1"));
            wrap.addEventListener("mouseleave", () => (label.style.opacity = "0"));

            wrap.addEventListener("click", () => onSelectRef.current?.(v));

            const marker = new mapboxgl.Marker({ element: wrap, anchor: "center" })
                .setLngLat([v.lng, v.lat])
                .addTo(map);
            markersRef.current.push(marker);
        });

        // Auto-fit / centre.
        if (geo.length >= 2) {
            const bounds = new mapboxgl.LngLatBounds();
            geo.forEach((v) => bounds.extend([v.lng, v.lat]));
            map.fitBounds(bounds, { padding: 80, maxZoom: 9, duration: 700 });
        } else if (geo.length === 1) {
            map.flyTo({ center: [geo[0].lng, geo[0].lat], zoom: 8, duration: 700 });
        }
    }, [vessels, stateMeta, mapReady]);

    // Toggle NOAA overlay visibility whenever the user flips the button.
    useEffect(() => {
        setNoaaVisibility(mapRef.current, noaaOn);
    }, [noaaOn, mapReady]);

    if (!TOKEN) {
        return (
            <div
                className="absolute inset-0 flex items-center justify-center border border-dashed border-virtus-line"
                data-testid="fleet-map-missing-token"
            >
                <div className="max-w-md text-center px-6">
                    <AlertTriangle size={40} className="mx-auto text-virtus-orange mb-4" />
                    <div className="font-heading text-xl uppercase tracking-tight text-white mb-2">
                        Mapbox token missing
                    </div>
                    <div className="text-sm text-virtus-dim">
                        Add <span className="text-virtus-orange font-mono">REACT_APP_MAPBOX_TOKEN</span>{" "}
                        to <span className="font-mono">frontend/.env</span> and restart the frontend.
                    </div>
                </div>
            </div>
        );
    }

    const noCoordsCount = vessels.filter((v) => !isNum(v.lat) || !isNum(v.lng)).length;

    return (
        <div className="absolute inset-0" data-testid="fleet-map-container">
            <div
                ref={containerRef}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: "100%",
                    height: "100%",
                }}
            />

            {/* Legend */}
            <div className="absolute top-3 left-3 border border-virtus-line bg-black/85 backdrop-blur px-3 py-2 z-10 pointer-events-none">
                <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-virtus-faint mb-2">
                    LEGEND
                </div>
                <div className="grid gap-1">
                    {Object.entries(stateMeta).map(([k, meta]) => (
                        <div key={k} className="flex items-center gap-2">
                            <div
                                style={{ background: "rgba(5,5,5,0.85)", border: `1.5px solid ${meta.bg}` }}
                                className="w-2.5 h-2.5 rotate-45"
                            />
                            <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-virtus-dim">
                                {meta.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Seamark overlay toggle (OpenSeaMap buoys, lights, harbor limits) */}
            <button
                onClick={() => setNoaaOn((v) => !v)}
                data-testid="fleet-map-noaa-toggle"
                title={noaaOn ? "Hide seamark overlay" : "Show seamark overlay (buoys, lights, harbor limits)"}
                className={`absolute top-3 right-14 z-10 flex items-center gap-1.5 border px-2.5 py-1.5 font-mono text-[9px] tracking-[0.28em] uppercase backdrop-blur transition-colors ${
                    noaaOn
                        ? "bg-virtus-orange border-virtus-orange text-black"
                        : "bg-black/85 border-virtus-line text-virtus-dim hover:text-white hover:border-virtus-orange"
                }`}
            >
                <Anchor size={10} />
                SEAMARKS
            </button>

            {/* No-coords badge */}
            {noCoordsCount > 0 && (
                <div
                    className="absolute bottom-3 left-3 border border-virtus-orange bg-black/85 backdrop-blur px-3 py-2 z-10 max-w-[280px]"
                    data-testid="fleet-map-no-coords-badge"
                >
                    <div className="flex items-center gap-2 mb-1">
                        <MapPin size={11} className="text-virtus-orange" />
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange">
                            {noCoordsCount} vessel{noCoordsCount === 1 ? "" : "s"} without coords
                        </div>
                    </div>
                    <div className="text-[11px] text-virtus-dim leading-snug">
                        Edit vessel &rarr; add LAT / LNG to plot on the chart.
                    </div>
                </div>
            )}
        </div>
    );
}
